import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { Image as NativeImage, Text, View } from 'react-native'
import { Image as ExpoImage } from 'expo-image'

import { Spinner } from '../../../components/ui/Spinner'
import { addBreadcrumb, captureMessage } from '../../../utils/sentry'
import { buildImageCandidateUrls } from '../utils/media'

interface MediaImageProps {
  source: { uri: string }
  contentType: string
  aspectRatio: number
  fallbackAspectRatio: number
}

type MediaImageState = {
  activeIndex: number
  hasError: boolean
  isLoading: boolean
  useNativeFallback: boolean
}

type MediaImageAction =
  | { type: 'advance' }
  | { type: 'fail' }
  | { type: 'loaded' }
  | { type: 'loading' }
  | { type: 'reset' }
  | { type: 'switchToNativeFallback' }

const INITIAL_STATE: MediaImageState = {
  activeIndex: 0,
  hasError: false,
  isLoading: true,
  useNativeFallback: false,
}

function mediaImageReducer(
  state: MediaImageState,
  action: MediaImageAction,
): MediaImageState {
  switch (action.type) {
    case 'advance':
      return {
        ...state,
        activeIndex: state.activeIndex + 1,
        isLoading: true,
      }
    case 'fail':
      return {
        ...state,
        hasError: true,
        isLoading: false,
      }
    case 'loaded':
      return {
        ...state,
        hasError: false,
        isLoading: false,
      }
    case 'loading':
      return {
        ...state,
        isLoading: true,
      }
    case 'reset':
      return INITIAL_STATE
    case 'switchToNativeFallback':
      return {
        ...state,
        activeIndex: 0,
        isLoading: true,
        useNativeFallback: true,
      }
    default:
      return state
  }
}

function MediaImageContent({
  source,
  contentType,
  aspectRatio,
  fallbackAspectRatio,
}: MediaImageProps) {
  const imageCandidateUrls = useMemo(
    () => buildImageCandidateUrls(source.uri, contentType),
    [contentType, source.uri],
  )
  const [state, dispatch] = useReducer(mediaImageReducer, INITIAL_STATE)
  const lastReportedSignatureRef = useRef<string | null>(null)

  const resolvedAspectRatio = aspectRatio || fallbackAspectRatio
  const activeUrl = imageCandidateUrls[state.activeIndex] ?? null

  useEffect(() => {
    addBreadcrumb('media', 'image_load_start', { sourceUri: source.uri })
  }, [source.uri])

  const reportError = useCallback(
    (reason: string) => {
      const signature = JSON.stringify({
        reason,
        sourceUri: source.uri,
      })
      if (lastReportedSignatureRef.current === signature) return
      lastReportedSignatureRef.current = signature

      console.error('[MediaImage] Load failed', {
        reason,
        sourceUri: source.uri,
        activeIndex: state.activeIndex,
        useNativeFallback: state.useNativeFallback,
      })
      captureMessage('Image load failed', 'error', {
        feature: 'media',
        tags: { player: 'image', reason },
        extra: {
          sourceUri: source.uri,
          activeIndex: state.activeIndex,
          useNativeFallback: state.useNativeFallback,
          totalCandidates: imageCandidateUrls.length,
        },
      })
    },
    [
      imageCandidateUrls.length,
      source.uri,
      state.activeIndex,
      state.useNativeFallback,
    ],
  )

  const advanceOrFail = useCallback(() => {
    if (state.activeIndex < imageCandidateUrls.length - 1) {
      dispatch({ type: 'advance' })
      return
    }

    if (!state.useNativeFallback) {
      dispatch({ type: 'switchToNativeFallback' })
      return
    }

    dispatch({ type: 'fail' })
    addBreadcrumb('media', 'image_error', { sourceUri: source.uri })
    reportError('all_candidates_failed')
  }, [
    imageCandidateUrls.length,
    reportError,
    source.uri,
    state.activeIndex,
    state.useNativeFallback,
  ])

  const handleLoadStart = useCallback(() => {
    dispatch({ type: 'loading' })
  }, [])

  const handleLoad = useCallback(() => {
    dispatch({ type: 'loaded' })
    addBreadcrumb('media', 'image_ready', { sourceUri: source.uri })
  }, [source.uri])

  if (state.hasError || activeUrl == null) {
    return (
      <View
        className="w-full items-center justify-center bg-gray-100"
        style={{ aspectRatio: resolvedAspectRatio }}
      >
        <Text className="text-sm text-gray-500">Unable to load image</Text>
      </View>
    )
  }

  return (
    <View
      className="w-full mb-5 bg-gray-100 relative overflow-hidden"
      style={{ aspectRatio: resolvedAspectRatio }}
    >
      {state.useNativeFallback ? (
        <NativeImage
          source={{ uri: activeUrl }}
          style={{ width: '100%', height: '100%' }}
          resizeMode="contain"
          onLoadStart={handleLoadStart}
          onLoad={handleLoad}
          onError={advanceOrFail}
        />
      ) : (
        <ExpoImage
          source={{ uri: activeUrl }}
          style={{ width: '100%', height: '100%' }}
          contentFit="contain"
          transition={200}
          onLoadStart={handleLoadStart}
          onLoad={handleLoad}
          onError={advanceOrFail}
        />
      )}

      {state.isLoading && (
        <View
          className="absolute inset-0 items-center justify-center bg-black/10"
          style={{ zIndex: 20, pointerEvents: 'none' }}
        >
          <Spinner size="large" />
        </View>
      )}
    </View>
  )
}

export function MediaImage(props: MediaImageProps) {
  return <MediaImageContent key={props.source.uri} {...props} />
}
